import React, { useState, useEffect, useRef } from 'react';
import {
    X, CheckCircle2, AlertTriangle, XCircle, Info, Loader2, Save, ServerCrash,
    ChevronDown, ChevronUp, Trash2, PlayCircle,
} from 'lucide-react';
import { FileAnalysisResultDto } from '../types';

interface UploadSummaryModalProps {
    isOpen: boolean;
    onClose: () => void;
    results: FileAnalysisResultDto[];
    incaricoId: number;
    context: 'collaboratore' | 'admin';
    onManualDateSave: (mediaId: number, tempId: string, date: string, time: string) => Promise<void>;
    onDiscard?: (mediaId: number, tempId: string) => Promise<void>;
    onSubmitForApproval?: (items: { mediaId: number; reason: string }[]) => Promise<void>;
    // Upload da File Manager: i duplicati si tengono direttamente invece di
    // andare in approvazione (pulsante "Tieni").
    onKeep?: (mediaId: number, tempId: string) => Promise<void>;
    onPollStatus?: (mediaId: number) => Promise<FileAnalysisResultDto>;
}

const formatExifDate = (iso?: string): string => {
    if (!iso) return '—';
    // Stringhe non valide (es. "undefinedT00:00") darebbero "NaN/NaN/NaN".
    if (typeof iso !== 'string' || iso.includes('undefined')) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    const dd = d.getDate().toString().padStart(2, '0');
    const mm = (d.getMonth() + 1).toString().padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = d.getHours().toString().padStart(2, '0');
    const min = d.getMinutes().toString().padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
};

export const UploadSummaryModal: React.FC<UploadSummaryModalProps> = ({
    isOpen,
    onClose,
    results,
    context,
    onManualDateSave,
    onDiscard,
    onSubmitForApproval,
    onKeep,
    onPollStatus,
}) => {
    const [keepingDuplicate, setKeepingDuplicate] = useState(false);
    const [liveResults, setLiveResults] = useState<FileAnalysisResultDto[]>([]);
    const [newlyResolved, setNewlyResolved] = useState<Set<number>>(new Set());
    const [discardedIds, setDiscardedIds] = useState<Set<number>>(new Set());

    // Data per singolo file
    const [manualDates, setManualDates] = useState<Record<number, { date: string; time: string }>>({});
    const [savingId, setSavingId] = useState<number | null>(null);
    const [savedIds, setSavedIds] = useState<Set<number>>(new Set());
    const [discardingId, setDiscardingId] = useState<number | null>(null);

    // Data comune per i NO_EXIF
    const [noExifChecked, setNoExifChecked] = useState<Set<number>>(new Set());
    const [bulkDate, setBulkDate] = useState('');
    const [bulkTime, setBulkTime] = useState('');
    const [isBulkSaving, setIsBulkSaving] = useState(false);
    const [okCollapsed, setOkCollapsed] = useState(false);

    // OUTSIDE_RANGE / DUPLICATE
    const [checkedOutside, setCheckedOutside] = useState<Set<number>>(new Set());
    const [checkedDuplicate, setCheckedDuplicate] = useState<Set<number>>(new Set());
    const [submittingOutside, setSubmittingOutside] = useState(false);
    const [submittingDuplicate, setSubmittingDuplicate] = useState(false);
    const [submittedOutside, setSubmittedOutside] = useState(false);
    const [submittedDuplicate, setSubmittedDuplicate] = useState(false);
    const [discardedOutsideIds, setDiscardedOutsideIds] = useState<Set<number>>(new Set());
    const [discardingOutside, setDiscardingOutside] = useState(false);
    const [discardedDuplicateIds, setDiscardedDuplicateIds] = useState<Set<number>>(new Set());
    const [discardingDuplicate, setDiscardingDuplicate] = useState(false);

    // Lightbox per l'anteprima; si chiude con la X o cliccando sullo sfondo.
    const [lightboxItem, setLightboxItem] = useState<FileAnalysisResultDto | null>(null);

    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const liveResultsRef = useRef<FileAnalysisResultDto[]>([]);
    liveResultsRef.current = liveResults;

    useEffect(() => {
        if (isOpen) {
            setLiveResults(results);
            setNewlyResolved(new Set());
            setNoExifChecked(new Set());
            setBulkDate('');
            setBulkTime('');
            setDiscardedIds(new Set());
            setDiscardedOutsideIds(new Set());
            setCheckedOutside(new Set());
            setSubmittedOutside(false);
            setDiscardedDuplicateIds(new Set());
            setCheckedDuplicate(new Set());
            setSubmittedDuplicate(false);
        }
    }, [isOpen, results]);

    useEffect(() => {
        if (!isOpen || !onPollStatus) return;
        const poll = async () => {
            const pending = liveResultsRef.current.filter(r => r.analysisStatus === 'PENDING_CALLBACK' && r.mediaId);
            if (pending.length === 0) { if (pollingRef.current) clearInterval(pollingRef.current); return; }
            const updates = await Promise.allSettled(
                pending.map(r => onPollStatus(r.mediaId!).then(dto => ({ ...dto, tempId: r.tempId })))
            );
            const resolved: number[] = [];
            const resolvedMap: Record<number, FileAnalysisResultDto> = {};
            updates.forEach(res => {
                if (res.status !== 'fulfilled') return;
                const dto = res.value;
                if (!dto || dto.analysisStatus === 'PENDING_CALLBACK') return;
                if (dto.mediaId) { resolvedMap[dto.mediaId] = dto; resolved.push(dto.mediaId); }
            });
            if (resolved.length > 0) {
                setLiveResults(prev => prev.map(r =>
                    r.mediaId && resolvedMap[r.mediaId] ? resolvedMap[r.mediaId] : r
                ));
                setNewlyResolved(prev => { const s = new Set(prev); resolved.forEach(id => s.add(id)); return s; });
            }
        };
        pollingRef.current = setInterval(poll, 4000);
        return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
    }, [isOpen, onPollStatus]);

    if (!isOpen) return null;

    const visibleResults = liveResults.filter(r => !discardedIds.has(r.mediaId!));
    const ok = visibleResults.filter(r => r.analysisStatus === 'OK');
    const noExif = visibleResults.filter(r => r.analysisStatus === 'NO_EXIF');
    const outsideRange = visibleResults.filter(r => r.analysisStatus === 'OUTSIDE_RANGE');
    const duplicates = visibleResults.filter(r => r.analysisStatus === 'DUPLICATE');
    const pending = visibleResults.filter(r => r.analysisStatus === 'PENDING_CALLBACK');
    const errors = visibleResults.filter(r => r.analysisStatus === 'ERROR');

    const handleManualDateChange = (mediaId: number, field: 'date' | 'time', value: string) =>
        setManualDates(prev => ({ ...prev, [mediaId]: { ...prev[mediaId], [field]: value } }));

    const handleSaveDate = async (mediaId: number, tempId: string) => {
        const entry = manualDates[mediaId] || { date: '', time: '' };
        if (!entry.date) return;
        setSavingId(mediaId);
        try {
            await onManualDateSave(mediaId, tempId, entry.date, entry.time || '00:00');
            setSavedIds(prev => new Set(prev).add(mediaId));
        } finally { setSavingId(null); }
    };

    const handleDiscard = async (mediaId: number, tempId: string) => {
        if (!onDiscard) return;
        setDiscardingId(mediaId);
        try {
            await onDiscard(mediaId, tempId);
            setDiscardedIds(prev => new Set(prev).add(mediaId));
            setNoExifChecked(prev => { const s = new Set(prev); s.delete(mediaId); return s; });
        } finally { setDiscardingId(null); }
    };

    // Selezione dei NO_EXIF
    const allNoExifUnsaved = noExif.filter(r => !savedIds.has(r.mediaId!));
    const toggleNoExifCheck = (id: number) => {
        setNoExifChecked(prev => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });
    };
    const toggleAllNoExif = () => {
        const allIds = allNoExifUnsaved.map(r => r.mediaId!);
        const allChecked = allIds.every(id => noExifChecked.has(id));
        setNoExifChecked(allChecked ? new Set() : new Set(allIds));
    };

    const handleBulkSave = async () => {
        if (!bulkDate || noExifChecked.size === 0) return;
        setIsBulkSaving(true);
        try {
            const toSave = noExif.filter(r => noExifChecked.has(r.mediaId!) && !savedIds.has(r.mediaId!));
            await Promise.all(toSave.map(r => onManualDateSave(r.mediaId!, r.tempId, bulkDate, bulkTime || '00:00')));
            setSavedIds(prev => { const s = new Set(prev); toSave.forEach(r => s.add(r.mediaId!)); return s; });
            setNoExifChecked(new Set());
        } finally { setIsBulkSaving(false); }
    };

    const handleBulkDiscard = async () => {
        if (!onDiscard || noExifChecked.size === 0) return;
        const toDiscard = noExif.filter(r => noExifChecked.has(r.mediaId!) && !savedIds.has(r.mediaId!));
        await Promise.all(toDiscard.map(r => handleDiscard(r.mediaId!, r.tempId)));
        setNoExifChecked(new Set());
    };

    const toggleCheck = (id: number, set: Set<number>, setter: React.Dispatch<React.SetStateAction<Set<number>>>) => {
        const next = new Set(set); if (next.has(id)) next.delete(id); else next.add(id); setter(next);
    };

    const handleSubmitOutside = async () => {
        if (!onSubmitForApproval || checkedOutside.size === 0) return;
        setSubmittingOutside(true);
        try {
            await onSubmitForApproval(Array.from(checkedOutside).map(mediaId => ({ mediaId, reason: 'OUTSIDE_RANGE' })));
            setSubmittedOutside(true); setCheckedOutside(new Set());
        } finally { setSubmittingOutside(false); }
    };

    const handleSubmitDuplicate = async () => {
        if (!onSubmitForApproval || checkedDuplicate.size === 0) return;
        setSubmittingDuplicate(true);
        try {
            await onSubmitForApproval(Array.from(checkedDuplicate).map(mediaId => ({ mediaId, reason: 'DUPLICATE' })));
            setSubmittedDuplicate(true); setCheckedDuplicate(new Set());
        } finally { setSubmittingDuplicate(false); }
    };

    const SectionHeader = ({ color, icon, title, count }: { color: string; icon: React.ReactNode; title: string; count: number }) => (
        <div className="flex items-center gap-2 mb-3">
            {icon}
            <h3 className="font-black text-slate-900 text-sm uppercase tracking-wide">{title}</h3>
            <span className={`ml-auto text-[11px] font-black px-2 py-0.5 rounded-full ${color}`}>{count}</span>
        </div>
    );

    const NewBadge = ({ mediaId }: { mediaId?: number }) =>
        mediaId && newlyResolved.has(mediaId)
            ? <span className="text-[9px] font-black bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full uppercase shrink-0">Nuovo</span>
            : null;

    // Anteprima cliccabile (solo per gli orfani, che hanno thumbnailUrl).
    const ThumbPreview = ({ r }: { r: FileAnalysisResultDto }) => {
        if (!r.thumbnailUrl) return null;
        const isImage = !r.fileType || r.fileType.startsWith('image/');
        return (
            <button
                type="button"
                onClick={e => { e.stopPropagation(); setLightboxItem(r); }}
                className="shrink-0 block cursor-zoom-in"
                title="Anteprima"
            >
                {isImage ? (
                    <img
                        src={r.thumbnailUrl}
                        alt=""
                        className="w-11 h-11 object-cover rounded-md border border-slate-200 bg-slate-50"
                        onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                    />
                ) : (
                    <div className="w-11 h-11 rounded-md border border-slate-200 bg-slate-100 flex items-center justify-center">
                        <PlayCircle size={18} className="text-slate-500" />
                    </div>
                )}
            </button>
        );
    };

    const stillPending = pending.length > 0;

    return (
        // Il click sullo sfondo chiude la modale.
        <div
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in slide-in-from-bottom-4 flex flex-col max-h-[90vh]"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3 shrink-0">
                    <div>
                        <h2 className="text-lg font-black text-slate-900">Riepilogo Caricamento</h2>
                        <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                            {liveResults.length} file analizzati
                            {stillPending && <><Loader2 size={11} className="animate-spin text-blue-500" /><span className="text-blue-600 font-bold">{pending.length} in conversione…</span></>}
                            {errors.length > 0 && <span className="text-slate-500 font-bold">· {errors.length} falliti</span>}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <div className="overflow-y-auto flex-1 p-5 space-y-6">

                    {/* PENDING_CALLBACK */}
                    {pending.length > 0 && (
                        <section>
                            <SectionHeader color="bg-blue-100 text-blue-700" icon={<Loader2 size={18} className="text-blue-500 animate-spin" />} title="In elaborazione" count={pending.length} />
                            <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl p-3 mb-3">
                                <Info size={14} className="text-blue-600 shrink-0 mt-0.5" />
                                <p className="text-xs text-blue-800">I video sono in conversione. Il riepilogo si aggiornerà automaticamente al termine.</p>
                            </div>
                            <div className="space-y-1.5">
                                {pending.map(r => (
                                    <div key={r.tempId} className="flex items-center gap-3 bg-blue-50 border border-blue-100 rounded-xl px-3 py-2.5">
                                        <Loader2 size={14} className="text-blue-500 shrink-0 animate-spin" />
                                        <span className="text-xs font-medium text-slate-700 truncate flex-1">{r.name || r.tempId}</span>
                                        <span className="text-[11px] font-bold text-blue-600 shrink-0">Analisi in corso…</span>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* ERROR */}
                    {errors.length > 0 && (
                        <section>
                            <SectionHeader color="bg-slate-100 text-slate-600" icon={<ServerCrash size={18} className="text-slate-500" />} title="Elaborazione fallita" count={errors.length} />
                            <div className="space-y-1.5">
                                {errors.map(r => (
                                    <div key={r.tempId} className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">
                                        <XCircle size={14} className="text-slate-400 shrink-0" />
                                        <span className="text-xs font-medium text-slate-600 truncate flex-1">{r.name || r.tempId}</span>
                                        <span className="text-[11px] font-bold text-slate-400 shrink-0">Errore</span>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* OK, collassabile */}
                    {ok.length > 0 && (
                        <section>
                            <button
                                onClick={() => setOkCollapsed(v => !v)}
                                className="w-full flex items-center gap-2 mb-3 hover:bg-slate-50 rounded-lg px-1 py-0.5 transition-colors"
                            >
                                <CheckCircle2 size={18} className="text-emerald-600 shrink-0" />
                                <span className="font-black text-slate-900 text-sm uppercase tracking-wide flex-1 text-left">Acquisiti con data EXIF</span>
                                <span className="text-[11px] font-black px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{ok.length}</span>
                                {okCollapsed ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronUp size={16} className="text-slate-400" />}
                            </button>
                            {!okCollapsed && (
                                <div className="space-y-1.5">
                                    {ok.map(r => (
                                        <div key={r.tempId} className="flex items-center gap-3 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2.5">
                                            <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />
                                            <span className="text-xs font-medium text-slate-700 truncate flex-1">{r.name || r.tempId}</span>
                                            <NewBadge mediaId={r.mediaId} />
                                            <span className="text-[11px] font-bold text-emerald-700 shrink-0">{formatExifDate(r.exifDate || r.date)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>
                    )}

                    {/* NO_EXIF: data comune, selezione e annulla */}
                    {noExif.length > 0 && (
                        <section>
                            <SectionHeader color="bg-amber-100 text-amber-700" icon={<AlertTriangle size={18} className="text-amber-500" />} title="Senza data EXIF" count={noExif.filter(r => !savedIds.has(r.mediaId!)).length} />

                            {/* Barra bulk: appare sempre per la selezione multipla */}
                            {allNoExifUnsaved.length > 1 && (
                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3 space-y-2">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
                                            checked={allNoExifUnsaved.length > 0 && allNoExifUnsaved.every(r => noExifChecked.has(r.mediaId!))}
                                            onChange={toggleAllNoExif}
                                        />
                                        <span className="text-xs font-bold text-amber-800">
                                            {noExifChecked.size > 0 ? `${noExifChecked.size} selezionati` : 'Seleziona tutti'}
                                        </span>
                                    </div>
                                    {noExifChecked.size > 0 && (
                                        <div className="flex gap-2 flex-wrap">
                                            <input
                                                type="date"
                                                className="text-sm p-2 border border-amber-300 rounded-lg bg-white focus:ring-2 focus:ring-amber-500 outline-none flex-1 min-w-[130px]"
                                                value={bulkDate}
                                                onChange={e => setBulkDate(e.target.value)}
                                            />
                                            <input
                                                type="time"
                                                className="text-sm p-2 border border-amber-300 rounded-lg bg-white focus:ring-2 focus:ring-amber-500 outline-none w-28"
                                                value={bulkTime}
                                                onChange={e => setBulkTime(e.target.value)}
                                            />
                                            <button
                                                onClick={handleBulkSave}
                                                disabled={!bulkDate || isBulkSaving}
                                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold disabled:opacity-50 bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                                            >
                                                {isBulkSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                                                Salva tutti ({noExifChecked.size})
                                            </button>
                                            {onDiscard && (
                                                <button
                                                    onClick={handleBulkDiscard}
                                                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-rose-700 hover:bg-rose-50 border border-rose-200 shrink-0"
                                                >
                                                    <Trash2 size={13} />
                                                    Annulla tutti ({noExifChecked.size})
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="space-y-3">
                                {noExif.map(r => {
                                    const mediaId = r.mediaId!;
                                    const saved = savedIds.has(mediaId);
                                    const isChecked = noExifChecked.has(mediaId);
                                    return (
                                        <div key={r.tempId} className={`bg-slate-50 border rounded-xl p-3 transition-colors ${isChecked && !saved ? 'border-amber-300 bg-amber-50/50' : 'border-slate-200'}`}>
                                            <div className="flex items-center gap-2 mb-2">
                                                {!saved && allNoExifUnsaved.length > 1 && (
                                                    <input
                                                        type="checkbox"
                                                        className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500 cursor-pointer shrink-0"
                                                        checked={isChecked}
                                                        onChange={() => toggleNoExifCheck(mediaId)}
                                                    />
                                                )}
                                                <ThumbPreview r={r} />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs font-bold text-slate-700 truncate">{r.name || r.tempId}</p>
                                                    {r.incaricoLabel && <p className="text-[10px] text-slate-500 font-medium truncate">{r.incaricoLabel}</p>}
                                                </div>
                                                {!saved && onDiscard && (
                                                    <button
                                                        onClick={() => handleDiscard(mediaId, r.tempId)}
                                                        disabled={discardingId === mediaId}
                                                        className="p-1.5 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors shrink-0"
                                                        title="Annulla questo file"
                                                    >
                                                        {discardingId === mediaId ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                                                    </button>
                                                )}
                                                {saved && <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />}
                                            </div>
                                            {!saved && (
                                                <div className="flex gap-2 items-center flex-wrap">
                                                    <input
                                                        type="date"
                                                        className="text-sm p-2 border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none flex-1 min-w-[120px]"
                                                        value={manualDates[mediaId]?.date || ''}
                                                        onChange={e => handleManualDateChange(mediaId, 'date', e.target.value)}
                                                    />
                                                    <input
                                                        type="time"
                                                        className="text-sm p-2 border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none w-28"
                                                        value={manualDates[mediaId]?.time || ''}
                                                        onChange={e => handleManualDateChange(mediaId, 'time', e.target.value)}
                                                    />
                                                    <button
                                                        onClick={() => handleSaveDate(mediaId, r.tempId)}
                                                        disabled={!manualDates[mediaId]?.date || savingId === mediaId}
                                                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold disabled:opacity-50 bg-blue-600 hover:bg-blue-700 text-white shrink-0"
                                                    >
                                                        {savingId === mediaId ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                                                        Salva
                                                    </button>
                                                </div>
                                            )}
                                            {saved && manualDates[mediaId]?.date && (
                                                <p className="text-xs text-emerald-600 font-bold">{formatExifDate(`${manualDates[mediaId]!.date}T${manualDates[mediaId]?.time || '00:00'}`)}</p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {/* OUTSIDE_RANGE */}
                    {outsideRange.filter(r => !discardedOutsideIds.has(r.mediaId!)).length > 0 && (
                        <section>
                            <SectionHeader color="bg-rose-100 text-rose-700" icon={<XCircle size={18} className="text-rose-500" />} title="Data fuori dal periodo dell'incarico" count={outsideRange.filter(r => !discardedOutsideIds.has(r.mediaId!)).length} />
                            {context === 'collaboratore' ? (
                                <>
                                    <div className="flex items-start gap-2 bg-rose-50 border border-rose-100 rounded-xl p-3 mb-3">
                                        <Info size={14} className="text-rose-600 shrink-0 mt-0.5" />
                                        <p className="text-xs text-rose-800">
                                            La data EXIF di questi file è <strong>fuori dal periodo dell'incarico</strong>. Puoi mandarli in approvazione al responsabile/titolare oppure scartarli.
                                        </p>
                                    </div>

                                    {submittedOutside ? (
                                        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                                            <CheckCircle2 size={16} className="text-emerald-600" />
                                            <p className="text-xs font-bold text-emerald-800">Inviati in approvazione al responsabile/titolare</p>
                                        </div>
                                    ) : (() => {
                                        const visibleOutside = outsideRange.filter(r => !discardedOutsideIds.has(r.mediaId!));
                                        const allOutsideChecked = visibleOutside.length > 0 && visibleOutside.every(r => checkedOutside.has(r.mediaId!));
                                        const handleDiscardCheckedOutside = async () => {
                                            if (!onDiscard || checkedOutside.size === 0) return;
                                            setDiscardingOutside(true);
                                            try {
                                                const toDiscard = visibleOutside.filter(r => checkedOutside.has(r.mediaId!));
                                                await Promise.all(toDiscard.map(r => onDiscard(r.mediaId!, r.tempId)));
                                                setDiscardedOutsideIds(prev => { const s = new Set(prev); toDiscard.forEach(r => s.add(r.mediaId!)); return s; });
                                                setCheckedOutside(new Set());
                                            } finally { setDiscardingOutside(false); }
                                        };
                                        return (
                                            <>
                                                {/* Barra selezione bulk */}
                                                <div className="flex items-center gap-2 mb-2 px-1">
                                                    <input
                                                        type="checkbox"
                                                        className="w-4 h-4 rounded border-slate-300 text-rose-600 focus:ring-rose-500 cursor-pointer"
                                                        checked={allOutsideChecked}
                                                        onChange={() => {
                                                            const ids = visibleOutside.map(r => r.mediaId!);
                                                            setCheckedOutside(allOutsideChecked ? new Set() : new Set(ids));
                                                        }}
                                                    />
                                                    <span className="text-xs font-bold text-slate-600">
                                                        {checkedOutside.size > 0 ? `${checkedOutside.size} selezionati` : 'Seleziona tutti'}
                                                    </span>
                                                </div>

                                                <div className="space-y-1.5 mb-3">
                                                    {visibleOutside.map(r => (
                                                        <div key={r.tempId} className={`flex items-center gap-3 border rounded-xl px-3 py-2.5 transition-colors ${checkedOutside.has(r.mediaId!) ? 'bg-rose-50 border-rose-200' : 'bg-white border-rose-100'}`}>
                                                            <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-rose-600 focus:ring-rose-500 cursor-pointer shrink-0"
                                                                checked={checkedOutside.has(r.mediaId!)}
                                                                onChange={() => toggleCheck(r.mediaId!, checkedOutside, setCheckedOutside)} />
                                                            <ThumbPreview r={r} />
                                                            <div className="flex-1 min-w-0">
                                                                <span className="text-xs font-medium text-slate-700 truncate block">{r.name || r.tempId}</span>
                                                                {r.incaricoLabel && <span className="text-[10px] text-slate-500 font-medium block truncate">{r.incaricoLabel}</span>}
                                                                {r.exifDate && <span className="text-[10px] text-rose-600 font-bold">{formatExifDate(r.exifDate)}</span>}
                                                            </div>
                                                            <NewBadge mediaId={r.mediaId} />
                                                        </div>
                                                    ))}
                                                </div>

                                                <div className="flex gap-2">
                                                    {onDiscard && (
                                                        <button
                                                            onClick={handleDiscardCheckedOutside}
                                                            disabled={checkedOutside.size === 0 || discardingOutside}
                                                            className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 rounded-xl text-xs font-black uppercase tracking-wide transition-colors"
                                                        >
                                                            {discardingOutside ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                                                            Scarta ({checkedOutside.size})
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={handleSubmitOutside}
                                                        disabled={checkedOutside.size === 0 || submittingOutside}
                                                        className="flex-[2] flex items-center justify-center gap-2 py-2.5 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white rounded-xl text-xs font-black uppercase tracking-wide transition-colors"
                                                    >
                                                        {submittingOutside && <Loader2 size={14} className="animate-spin" />}
                                                        Manda in approvazione ({checkedOutside.size})
                                                    </button>
                                                </div>
                                            </>
                                        );
                                    })()}
                                </>
                            ) : (
                                <div className="space-y-1.5">
                                    {outsideRange.filter(r => !discardedOutsideIds.has(r.mediaId!)).map(r => (
                                        <div key={r.tempId} className="flex items-center gap-3 bg-orange-50 border border-orange-100 rounded-xl px-3 py-2.5">
                                            <AlertTriangle size={14} className="text-orange-500 shrink-0" />
                                            <span className="text-xs font-medium text-slate-700 truncate flex-1">{r.name || r.tempId}</span>
                                            <NewBadge mediaId={r.mediaId} />
                                            {r.exifDate && <span className="text-[11px] text-orange-600 font-bold shrink-0">{formatExifDate(r.exifDate)}</span>}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>
                    )}

                    {/* DUPLICATE */}
                    {duplicates.filter(r => !discardedDuplicateIds.has(r.mediaId!)).length > 0 && (
                        <section>
                            <SectionHeader color="bg-rose-100 text-rose-700" icon={<XCircle size={18} className="text-rose-500" />} title="Possibili duplicati" count={duplicates.filter(r => !discardedDuplicateIds.has(r.mediaId!)).length} />
                            <div className="flex items-start gap-2 bg-rose-50 border border-rose-100 rounded-xl p-3 mb-3">
                                <Info size={14} className="text-rose-600 shrink-0 mt-0.5" />
                                <p className="text-xs text-rose-800">{onKeep
                                    ? 'Questi file risultano identici ad altri già presenti. Tienili comunque se ti servono, oppure scartali.'
                                    : 'Un duplicato potrebbe essere un aggiornamento dello stesso file. Mandalo in approvazione se vuoi tenerlo, oppure scartalo.'}</p>
                            </div>
                            {submittedDuplicate ? (
                                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                                    <CheckCircle2 size={16} className="text-emerald-600" />
                                    <p className="text-xs font-bold text-emerald-800">Inviati in approvazione</p>
                                </div>
                            ) : (() => {
                                const visibleDuplicates = duplicates.filter(r => !discardedDuplicateIds.has(r.mediaId!));
                                const allDuplicateChecked = visibleDuplicates.length > 0 && visibleDuplicates.every(r => checkedDuplicate.has(r.mediaId!));
                                const handleDiscardCheckedDuplicate = async () => {
                                    if (!onDiscard || checkedDuplicate.size === 0) return;
                                    setDiscardingDuplicate(true);
                                    try {
                                        const toDiscard = visibleDuplicates.filter(r => checkedDuplicate.has(r.mediaId!));
                                        await Promise.all(toDiscard.map(r => onDiscard(r.mediaId!, r.tempId)));
                                        setDiscardedDuplicateIds(prev => { const s = new Set(prev); toDiscard.forEach(r => s.add(r.mediaId!)); return s; });
                                        setCheckedDuplicate(new Set());
                                    } finally { setDiscardingDuplicate(false); }
                                };
                                return (
                                    <>
                                        {/* Barra selezione bulk */}
                                        <div className="flex items-center gap-2 mb-2 px-1">
                                            <input
                                                type="checkbox"
                                                className="w-4 h-4 rounded border-slate-300 text-rose-600 focus:ring-rose-500 cursor-pointer"
                                                checked={allDuplicateChecked}
                                                onChange={() => {
                                                    const ids = visibleDuplicates.map(r => r.mediaId!);
                                                    setCheckedDuplicate(allDuplicateChecked ? new Set() : new Set(ids));
                                                }}
                                            />
                                            <span className="text-xs font-bold text-slate-600">
                                                {checkedDuplicate.size > 0 ? `${checkedDuplicate.size} selezionati` : 'Seleziona tutti'}
                                            </span>
                                        </div>

                                        <div className="space-y-1.5 mb-3">
                                            {visibleDuplicates.map(r => (
                                                <div key={r.tempId} className={`flex items-center gap-3 border rounded-xl px-3 py-2.5 transition-colors ${checkedDuplicate.has(r.mediaId!) ? 'bg-rose-50 border-rose-200' : 'bg-white border-rose-100'}`}>
                                                    <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-rose-600 focus:ring-rose-500 cursor-pointer shrink-0"
                                                        checked={checkedDuplicate.has(r.mediaId!)}
                                                        onChange={() => toggleCheck(r.mediaId!, checkedDuplicate, setCheckedDuplicate)} />
                                                    <ThumbPreview r={r} />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs font-medium text-slate-700 truncate">{r.name || r.tempId}</p>
                                                        {r.incaricoLabel && <p className="text-[10px] text-slate-500 font-medium truncate">{r.incaricoLabel}</p>}
                                                        {(r as any).duplicateOfName && <p className="text-[10px] text-rose-500 font-bold">Duplicato di: {(r as any).duplicateOfName}</p>}
                                                    </div>
                                                    <NewBadge mediaId={r.mediaId} />
                                                </div>
                                            ))}
                                        </div>

                                        <div className="flex gap-2">
                                            {onDiscard && (
                                                <button
                                                    onClick={handleDiscardCheckedDuplicate}
                                                    disabled={checkedDuplicate.size === 0 || discardingDuplicate}
                                                    className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 rounded-xl text-xs font-black uppercase tracking-wide transition-colors"
                                                >
                                                    {discardingDuplicate ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                                                    Scarta ({checkedDuplicate.size})
                                                </button>
                                            )}
                                            {onKeep ? (
                                                // File Manager: il titolare tiene il duplicato direttamente,
                                                // senza passare per l'approvazione.
                                                <button
                                                    onClick={async () => {
                                                        if (checkedDuplicate.size === 0) return;
                                                        setKeepingDuplicate(true);
                                                        try {
                                                            const toKeep = visibleDuplicates.filter(r => checkedDuplicate.has(r.mediaId!));
                                                            await Promise.all(toKeep.map(r => onKeep(r.mediaId!, r.tempId)));
                                                            setDiscardedDuplicateIds(prev => { const s = new Set(prev); toKeep.forEach(r => s.add(r.mediaId!)); return s; });
                                                            setCheckedDuplicate(new Set());
                                                        } finally { setKeepingDuplicate(false); }
                                                    }}
                                                    disabled={checkedDuplicate.size === 0 || keepingDuplicate}
                                                    className="flex-[2] flex items-center justify-center gap-2 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl text-xs font-black uppercase tracking-wide transition-colors">
                                                    {keepingDuplicate ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                                                    Tieni ({checkedDuplicate.size})
                                                </button>
                                            ) : (
                                                <button onClick={handleSubmitDuplicate} disabled={checkedDuplicate.size === 0 || submittingDuplicate}
                                                    className="flex-[2] flex items-center justify-center gap-2 py-2.5 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white rounded-xl text-xs font-black uppercase tracking-wide transition-colors">
                                                    {submittingDuplicate && <Loader2 size={14} className="animate-spin" />}
                                                    Manda in approvazione ({checkedDuplicate.size})
                                                </button>
                                            )}
                                        </div>
                                    </>
                                );
                            })()}
                        </section>
                    )}

                    {visibleResults.length === 0 && (
                        <div className="text-center py-12 text-slate-400">
                            <CheckCircle2 size={40} className="mx-auto mb-3 text-slate-200" />
                            <p className="text-sm font-bold">Nessun risultato</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-slate-100 shrink-0">
                    <button onClick={onClose} className="w-full py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors border border-slate-200">
                        {stillPending ? 'Chiudi (elaborazione in background)' : 'Chiudi'}
                    </button>
                </div>
            </div>

            {/* Lightbox: si chiude con sfondo o X; lo stopPropagation interno evita
                la chiusura cliccando sul contenuto. */}
            {lightboxItem && lightboxItem.thumbnailUrl && (
                <div
                    className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
                    onClick={() => setLightboxItem(null)}
                >
                    <button
                        onClick={e => { e.stopPropagation(); setLightboxItem(null); }}
                        className="absolute top-4 right-4 p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors z-10"
                        title="Chiudi"
                    >
                        <X size={28} />
                    </button>
                    <div
                        className="max-w-[90vw] max-h-[90vh] flex items-center justify-center"
                        onClick={e => e.stopPropagation()}
                    >
                        {(!lightboxItem.fileType || lightboxItem.fileType.startsWith('image/')) ? (
                            <img
                                src={lightboxItem.thumbnailUrl}
                                alt={lightboxItem.name || ''}
                                className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
                            />
                        ) : (
                            <video
                                src={lightboxItem.thumbnailUrl}
                                controls
                                autoPlay
                                className="max-w-full max-h-[90vh] rounded-lg shadow-2xl bg-black"
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UploadSummaryModal;
